<script setup lang="ts">
import { LocationClient, SearchPlaceIndexForTextCommand } from '@aws-sdk/client-location';
import { withAPIKey } from '@aws/amazon-location-utilities-auth-helper';
import type { Location } from '@/modules/content-blocks/interfaces/location-block.interface';
import { formatAddress } from '@/modules/content-blocks/helpers/location-block.helper';
import { AWS_LOCATION_CONFIG } from './location/aws-location.config';
import type { Coordinates } from './location/maps.interface';
import MapContainer from './location/MapContainer.vue';
import MapCard from './location/LocationCard.vue';

const viewport = inject<'desktop' | 'tablet' | 'mobile'>('viewport');

const props = defineProps({
   data: {
      type: Object,
      default: () => ({}),
   },
});

const locations = computed(() => {
   return (props.data.locations as Location[]) || [];
});

const client = ref<LocationClient | null>(null);
const mapContainerRef = ref<InstanceType<typeof MapContainer>>();
const selectedLocationId = ref<string | null>(null);
const geocodedLocations = ref<Map<string, Coordinates>>(new Map());

const setupLocationClient = async (): Promise<void> => {
   const auth = await withAPIKey(AWS_LOCATION_CONFIG.apiKey);
   client.value = new LocationClient({
      region: AWS_LOCATION_CONFIG.region,
      ...auth.getLocationClientConfig(),
   });
};

const getGeocode = async (address: string): Promise<Coordinates | null> => {
   if (!client.value) return null;

   // convert an address to lat/lng
   const command = new SearchPlaceIndexForTextCommand({
      IndexName: AWS_LOCATION_CONFIG.placesIndex,
      Text: address,
      MaxResults: 1,
   });

   const response = await client.value.send(command);
   const point = response?.Results?.[0]?.Place?.Geometry?.Point;

   if (point && point.length === 2) {
      return {
         lng: point[0],
         lat: point[1],
      };
   }

   return null;
};

const handleLocationCardClick = async (locationId: string): Promise<void> => {
   const location = locations.value.find((loc) => loc.id === locationId);

   if (!location) return;

   selectedLocationId.value = location.id;

   const addressString = formatAddress(location.address);
   if (!addressString) return;

   // try to use already geocoded point (on mount)
   let coords = geocodedLocations.value.get(addressString);
   // else try to get it (again)
   if (!coords) {
      const geocodedCoords = await getGeocode(addressString);
      if (geocodedCoords) {
         coords = geocodedCoords;
         geocodedLocations.value.set(addressString, coords);
      }
   }

   // center on the location
   if (coords && mapContainerRef.value) {
      mapContainerRef.value.centerOn(coords.lng, coords.lat);
   }
};

onMounted(async () => {
   if (!locations.value?.length) return;
   await setupLocationClient();

   // geocode all addresses
   for (const location of locations.value) {
      const addressString = formatAddress(location.address);
      // next, nothing to geocode
      if (!addressString || geocodedLocations.value.has(addressString)) {
         continue;
      }

      const coords = await getGeocode(addressString);
      if (coords) {
         geocodedLocations.value.set(addressString, coords);
      }
   }
});

const locationsWithGeocoding = computed(() => {
   if (!locations.value?.length) return [];

   // filter locations that have an address
   return (
      locations.value
         .filter((location) => !!formatAddress(location.address))
         .map((location) => {
            const addressString = formatAddress(location.address);
            const coords = geocodedLocations.value.get(addressString);
            if (!coords) return null;
            return coords;
         })
         // and have a geocoded location
         .filter((loc) => !!loc)
   );
});
</script>

<template>
   <section v-if="locations?.length" :class="['location-content', { 'location-content--mobile': viewport === 'mobile' }]">
      <div class="location-content__container">
         <div class="location-content__map">
            <MapContainer ref="mapContainerRef" :locations="locationsWithGeocoding" />
         </div>
         <div class="location-content__list">
            <MapCard
               v-for="location in locations"
               :key="location.id"
               :location="location"
               :class="{ 'is-selected': selectedLocationId === location.id }"
               @onSelectLocation="handleLocationCardClick"
            />
         </div>
      </div>
   </section>
   <section v-else class="location-content location-content--empty">
      {{ $tb('live-editor.content-blocks.location.no-locations-configured') }}
   </section>
</template>

<style scoped lang="scss">
.location-content {
   &__container {
      display: flex;
      gap: var(--nxt-gutter);
      max-width: 1000px;
      margin: 0 auto;
   }

   &__map {
      flex: 7 1 0;
      height: 380px;
      border-radius: var(--nxt-radius);
      overflow: hidden;
   }

   &__list {
      flex: 3 1 0;
      display: flex;
      flex-direction: column;
      gap: var(--nxt-gutter-large);
      height: 380px;
      overflow-y: auto;
   }

   @media (max-width: 768px) {
      &__container {
         flex-direction: column;
      }

      &__map {
         flex: none;
         width: 100%;
         height: 300px;
      }

      &__list {
         flex: none;
         width: 100%;
         height: auto;
         overflow-y: visible;
      }
   }

   @media (max-width: 400px) {
      &__container {
         flex-direction: column;
      }

      &__map {
         flex: none;
         width: 100%;
         height: 200px;
      }

      &__list {
         flex: none;
         width: 100%;
         height: auto;
         overflow-y: visible;
      }
   }

   &.location-content--mobile {
      .location-content__container {
         flex-direction: column;
      }

      .location-content__map {
         flex: none;
         width: 100%;
         height: 200px;
      }

      .location-content__list {
         flex: none;
         width: 100%;
         height: auto;
         overflow-y: visible;
      }
   }
}
</style>
